// This is copied from site-scanner. Planning to move to ui-common.

import { FC, ReactElement, useCallback, useEffect, useRef, useState } from 'react';

import classNames from 'classnames';

import {
	EV_SPINNER_SIZES,
	EvIcon,
	EvSpinner,
	NoScreenShotImageIcon
} from '@evinced-private/ui-common';

import { Logger } from 'src/common/services/Logger';
import { ScreenshotDetails, screenshotService } from 'src/common/services/ScreenshotService';

import './ScreenshotCellFormatter.scss';

interface IScreenshotCellFormatterProps {
	cellDescriptor: string;
	screenshotDetails: ScreenshotDetails;
	imageContainerClassName?: string;
	screenshotImgClassName?: string;
	noCrop?: boolean;
}
export const ScreenshotCellFormatter: FC<IScreenshotCellFormatterProps> = ({
	screenshotDetails,
	cellDescriptor,
	imageContainerClassName,
	screenshotImgClassName,
	noCrop = false
}: IScreenshotCellFormatterProps) => {
	const [isLoading, setLoading] = useState(true);
	const [image, setImage] = useState<string>(null);
	const isMounted = useRef(false);

	const resolveImage = useCallback(async (): Promise<void> => {
		try {
			const screenshotImageData = screenshotDetails
				? await screenshotService.getIssueOnlyScreenshot(screenshotDetails, 150, noCrop)
				: null;
			// use isMounted to avoid setting state of an unmounted component
			if (!isMounted.current) {
				return;
			}
			setImage(screenshotImageData);
		} catch (e) {
			Logger.warn(e);
			setImage(null);
		}
		setLoading(false);
	}, [screenshotDetails, isMounted, noCrop]);

	// resolving image when url/selector is changed.
	// this makes the cell re-render when paginating
	useEffect(() => {
		isMounted.current = true;
		if (!image) {
			resolveImage();
		}
		return () => {
			isMounted.current = false;
		};
	}, [image, resolveImage]);

	const renderImagePlaceholder = (): ReactElement => {
		return (
			<EvIcon
				icon={NoScreenShotImageIcon}
				className="no-screenshot-image-icon"
				title="not available"
			/>
		);
	};

	const renderImageButton = (): ReactElement => {
		if (!image) {
			return renderImagePlaceholder();
		}

		return (
			<img
				src={image}
				alt={`${cellDescriptor} issue screenshot`}
				aria-label={`${cellDescriptor} issue details`}
				className={classNames('screenshot-cell-img', {
					[screenshotImgClassName]: !!screenshotImgClassName
				})}
			/>
		);
	};

	return (
		<div className="screenshot-cell">
			<div
				className={classNames('image-container', {
					[imageContainerClassName]: !!imageContainerClassName
				})}
			>
				{isLoading ? <EvSpinner size={EV_SPINNER_SIZES.XSMALL} /> : renderImageButton()}
			</div>
		</div>
	);
};
